import pluralize from 'pluralize';
import {
  orderBy, keyBy, cloneDeep, concat, camelCase,
  each, filter, reduce, map, values, find, indexOf,
  findIndex, pick, first, omit, uniq, compact,
} from 'lodash';
import moment from 'moment';
import {
  DASHBOARD_RESET_ALL,
  DASHBOARD_FETCH_PIPELINE_OPPORTUNITIES,
  DASHBOARD_FETCH_FORECAST_OPPORTUNITIES,
  DASHBOARD_FETCH_CONVERSION_OPPORTUNITIES,
  DASHBOARD_FETCH_CURRENT_GLOBAL_RANKINGS,
  DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITIES,
  DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_FEEDBACKS,
  DASHBOARD_REQUEST_EDIT_WIN_LOSS_OPPORTUNITY_FEEDBACKS,
  DASHBOARD_REQUEST_EDIT_WIN_LOSS_ACCOUNT_FEEDBACKS,
  DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_FEEDBACK,
  DASHBOARD_REQUEST_LOAD_WIN_LOSS_OPPORTUNITY_CONTACTS,
  DASHBOARD_REQUEST_LOAD_WIN_LOSS_ACCOUNT_CONTACTS,
  DASHBOARD_REQUEST_LOAD_OPPORTUNITY_INSIGHTS,
  DASHBOARD_REQUEST_LOAD_ACCOUNT_INSIGHTS,
  DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_SURVEYS,
  DASHBOARD_FETCH_WIN_LOSS_INTERNAL_EMAILS,
  DASHBOARD_REQUEST_SHOW_WIN_LOSS_SURVEY_EMAIL,
  DASHBOARD_REQUEST_CANCEL_WIN_LOSS_SURVEY,
  DASHBOARD_REQUEST_DELETE_WIN_LOSS_SURVEY,
  DASHBOARD_REQUEST_WIN_LOSS_SEND_NEXT_EMAIL,
  DASHBOARD_FETCH_WIN_LOSS_GLOBAL_RANKINGS,
  DASHBOARD_REQUEST_TOGGLE_PAUSE_WIN_LOSS_SURVEY,
  DASHBOARD_REQUEST_RESCHEDULE_WIN_LOSS_SURVEY,
  DASHBOARD_WIN_LOSS_EXCLUDE_OPPORTUNITY,
  DASHBOARD_WIN_LOSS_INCLUDE_OPPORTUNITY,
  DASHBOARD_WIN_LOSS_EXCLUDE_ACCOUNT,
  DASHBOARD_WIN_LOSS_INCLUDE_ACCOUNT,
  DASHBOARD_FETCH_WIN_LOSS_ACCOUNT_RESULTS,
  DASHBOARD_FETCH_WIN_LOSS_ACCOUNT_JOBS,
  DASHBOARD_ADD_WIN_LOSS_ACCOUNT_JOB,
  DASHBOARD_DELETE_WIN_LOSS_ACCOUNT_JOB,
  DASHBOARD_UPDATE_WIN_LOSS_ACCOUNT_STATUS,
  DASHBOARD_FETCH_COLLECTOR_SUMMARY,
  DASHBOARD_FETCH_COLLECTOR_STATS,
  DASHBOARD_REQUEST_CHANGE_CONTACT_SENDER,
  DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_SELLER_FEEDBACKS,
  DASHBOARD_FEEDBACK_REQUEST_SETTINGS,
  DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK,
  DASHBOARD_FETCH_OPPORTUNITY_FEEDBACK_REQUEST_STATUS,
  DASHBOARD_FETCH_EXCLUDED_OPPORTUNITIES,
  DASHBOARD_FETCH_EXCLUDED_CONTACTS,
  DASHBOARD_CHECK_QUALIFIED_CHUNK,
  DASHBOARD_REQUEST_CHANGE_ACCOUNT_SIZE,
  DASHBOARD_FETCH_PROGRESS_CONTACTS,
  DASHBOARD_APPEND_OPPORTUNITY_CONTACT_ROLE,
} from './DashboardActions';

import { ADD_OPPORTUNITY_CONTACT, ADD_ACCOUNT_CONTACT } from '../../components/add-opportunity-contact/actions';

// import CliTable from 'cli-table2';
// import numeral from 'numeral';

import {
  calculateProjectedSales,
  getStatsFromOpportunities,
  conversionGraphReducer,
} from '../Pipeline/PipelineReducer';

const oneProgressStats = {
  count: 0, 
  ids: [], 
  won: { count: 0, ids: [] },
  lost: { count: 0, ids: [] },
  noDecision: { count: 0, ids: [] },
  renewal: { count: 0, ids: [] },
  newBusiness: { count: 0, ids: [] },
  addOn: { count: 0, ids: [] },
  upSell: { count: 0, ids: [] },
};

const emptyProgressStats = {
  all: cloneDeep(oneProgressStats),
  qualified: cloneDeep(oneProgressStats), // Count of opportunities that are qualified based on the selected automation group
  submitted: cloneDeep(oneProgressStats), // Count of opportunities that have submitted at least one contact for feedback
  excluded: cloneDeep(oneProgressStats), // The entire opportunity was excluded by a user, OR All submitted contacts for an opportunity were terminated
  notSubmitted: cloneDeep(oneProgressStats), // qualified based on the selected automation group, but have not submitted at least one contact for feedback
  notQualified: cloneDeep(oneProgressStats), //  count of opportunities that are not qualified based on the selected automation group
  allSubmitted: cloneDeep(oneProgressStats), // regarless of match or not

  'notSubmitted/noQualifiedContacts': cloneDeep(oneProgressStats),
  'notSubmitted/preventedByCollectorSettings': cloneDeep(oneProgressStats),
  'notSubmitted/failedProductConditions': cloneDeep(oneProgressStats),
  'notSubmitted/invalidOwners': cloneDeep(oneProgressStats),
  'notSubmitted/invalidSenders': cloneDeep(oneProgressStats),
  'notSubmitted/invalidAccounts': cloneDeep(oneProgressStats),
  'notSubmitted/others': cloneDeep(oneProgressStats),
};

// Initial State
const emptyState = {
  teams: {
    status: null,
    errors: null,
    teams: [],
    indexedUsers: {},
    indexedOwners: {},
  },
  pipeline: {
    opportunities: [],
    owners: {}, // indexed owners
    accounts: [], // list
    stages: [],
    ownerStages: [],
    status: null,
    errors: null,
    totalStats: {},
    indexedOwnerStats: {}, // indexedByOwnerID
    rankings: [],
  },
  forecast: {
    status: null,
    errors: null,
    forecasts: [],
    allOpportunities: [], // include untracked
    stats: {
      amount: 0,
      count: 0,
    },
    ownerStats: {}, // indexedByOwnerID
    rankings: [],
  },
  conversion: {
    status: null,
    errors: null,
    opportunities: [],
    average: [], // [ { stage 1}, { stage 2} ]
    owners: {}, // { ownerId: [{ stage 1}, { stage 2}], ownerId: [{stage 1}, {stage 2}] }
    ownerProjectedSales: {},
    projectedSales: {},
    rankings: [],
  },
  rankings: {
    status: null,
    errors: null,
    owners: [],
  },
  winLossRankings: {
    status: null,
    errors: null,
    owners: [],
  },
  winLoss: {
    status: null,
    errors: null,
    opportunities: [],
    ownerStats: {},
    stats: {},
    accounts: [],
    accountResults: {},
    accountJobs: {},
    reasonStats: {},
    rankings: {},
    editingOpportunity: null,
    editingAccount: null,
    feedbacks: {}, // indexed by opportunityId
    sellerFeedbacks: {}, // indexed by opportunityId
    surveys: {}, // indexed by opportunityId
    internalEmails: {}, // indexed by opportunityId
    feedbackStatus: null,
    totalExclude: 0,
    currentInsight: {
      status: null,
      errors: null,
      data: null,
    },
    currentEmail: {},
    statsFilters: {}, // need reasons, refused, non-responsive, etc...
    indexedCollectorStats: {},
    currentCollectorSummary: [], // array of steps current popup


    progressLoadPercentage: 0,
    progressLoadError: null,
    progressStats: {
      ...emptyProgressStats,
    },
  },
  exclusions: {
    status: null,
    errors: null,
    opportunities: [],
    stats: {}, // won/lost/nd
  },
  excludedContacts: {
    status: null,
    errors: null,
    contacts: [],
  },
  progressContacts: {
    status: null,
    errors: null,
    contacts: [],
  },
  collectorStats: {

  }, // indexed by collectorId
  settings: {},
  feedbackRequestStatus: {}, // indexed by opportunityId
};

const initialState = cloneDeep(emptyState);

function pipelineReducer(action) {
  const {
    status, errors, opportunities,
    owners, accounts, stages, industries,
  } = action;

  const indexedAverageStages = {};

  const allStages = map(stages, (s) => {
    const stage = {
      ...s,
      totalAmount: 0,
      totalCount: 0,
    };
    indexedAverageStages[s.value] = stage;
    return stage;
  });

  const ownerStages = {};
  const ownerIndexedStages = {};

  each(accounts, (account) => {
    if (account.industries) {
      account.industry_names = reduce(account.industries, (items, i) => {
        if (industries[i]) {
          return concat(items, industries[i]);
        }
        return items;
      }, []);
    }
    if (owners[account.owner_id]) {
      account.owner = owners[account.owner_id];
    }
  });

  const cb = (o) => {
    const closeDate = o.close_date ? moment(o.close_date, 'YYYY-MM-DD') : null;
    const createdDate = o.created_date ? moment(o.created_date, 'YYYY-MM-DD') : null;
    const lastActivityDate = o.last_activity_date ? moment(o.last_activity_date, 'YYYY-MM-DD') : null;
    o.close_date = closeDate;
    o.created_date = createdDate;
    o.last_activity_date = lastActivityDate;
    if (closeDate) {
      o.close_date_quarterly = closeDate.format('Y \\QQ');
      o.close_date_monthly = closeDate.format('Y MM');
    }
    const amount = o.amount || 0;
    const stageName = o.stage_name;

    if (owners[o.owner_id]) {
      o.owner = owners[o.owner_id];
    }
    if (accounts[o.account_id]) {
      o.account = accounts[o.account_id];
    }
    if (indexedAverageStages[stageName]) {
      indexedAverageStages[stageName].totalAmount += amount;
      indexedAverageStages[stageName].totalCount += 1;
    }
    if (!ownerStages[o.owner_id]) {
      if (!ownerIndexedStages[o.owner_id]) {
        ownerIndexedStages[o.owner_id] = {};
      }
      ownerStages[o.owner_id] = map(stages, (s) => {
        const stage = {
          ...s,
          totalAmount: 0,
          totalCount: 0,
        };
        ownerIndexedStages[o.owner_id][s.value] = stage;
        return stage;
      });
    }
    if (ownerIndexedStages[o.owner_id][stageName]) {
      ownerIndexedStages[o.owner_id][stageName].totalAmount += amount;
      ownerIndexedStages[o.owner_id][stageName].totalCount += 1;
    }
    return o;
  };

  const stats = getStatsFromOpportunities(opportunities, cb);
  const { indexedOwnerStats, totalStats } = stats;

  const rankings = orderBy(reduce(indexedOwnerStats, (items, s, ownerId) => items.concat({
    owner: owners[ownerId],
    amount: s.amount,
    count: s.count,
  }), []), ['amount'], ['desc']);

  return {
    opportunities,
    totalStats,
    indexedOwnerStats,
    stages: allStages,
    ownerStages,
    status,
    errors,
    owners,
    rankings,
    accounts: values(accounts),
  };
}

function forecastReducer(action) {
  const {
    forecasts, owners, status, errors,
    untrackedOpportunities, accounts,
  } = action;
  const onStat = {
    count: 0,
    amount: 0,
    ids: [],
    won: {
      amount: 0,
      count: 0,
      ids: [],
    },
    categories: {}, // by forecast category
  };

  const stats = cloneDeep(onStat);
  const ownerStats = {};
  const allOpportunities = concat(forecasts, untrackedOpportunities);
  each(allOpportunities, (opportunity) => {
    const amount = opportunity.opportunity_amount || 0; // current amount
    const forecastCategory = opportunity.opportunity_forecast; // current status?
    const isCurrent = !opportunity.is_closed && !opportunity.is_slipped;
    const isWon = opportunity.stage === 'Won' && opportunity.is_closed;
    const ownerId = opportunity.owner_id;
    const opportunityId = opportunity.opportunity_id;
    const closeDate = opportunity.close_date ? moment(opportunity.close_date, 'YYYY-MM-DD') : null;
    const createdDate = opportunity.created_date ? moment(opportunity.created_date, 'YYYY-MM-DD') : null;
    opportunity.close_date = closeDate;
    opportunity.created_date = createdDate;
    const opportunityCloseDate = opportunity.opportunity_close_date ? moment(opportunity.opportunity_close_date, 'YYYY-MM-DD') : null;
    const opportunityCreatedDate = opportunity.opportunity_created_date ? moment(opportunity.opportunity_created_date, 'YYYY-MM-DD') : null;
    opportunity.opportunity_close_date = opportunityCloseDate;
    opportunity.opportunity_created_date = opportunityCreatedDate;
    opportunity.owner = owners[ownerId];
    opportunity.account = accounts[opportunity.account_id];
    if (!ownerStats[ownerId]) {
      ownerStats[ownerId] = cloneDeep(onStat);
    }
    const ownerStat = ownerStats[ownerId];

    each([stats, ownerStat], (st) => {
      let shouldCount = false;
      if (isWon) {
        st.won.amount += amount;
        st.won.count += 1;
        st.won.ids.push(opportunityId);
        shouldCount = true;
      } else if (isCurrent && forecastCategory !== 'Omitted') {
        if (!st.categories[forecastCategory]) {
          st.categories[forecastCategory] = { count: 0, amount: 0, ids: [] };
        }
        const categoryStats = st.categories[forecastCategory];
        categoryStats.count += 1;
        categoryStats.amount += amount;
        categoryStats.ids.push(opportunityId);
        shouldCount = true;
      }
      if (shouldCount) {
        st.amount += amount;
        st.count += 1;
        st.ids.push(opportunityId);
      }
    });
  });

  const rankings = orderBy(reduce(ownerStats, (items, s, ownerId) => items.concat({
    owner: owners[ownerId],
    amount: s.amount,
    count: s.count,
  }), []), ['amount'], ['desc']);

  return {
    status,
    errors,
    forecasts,
    stats,
    ownerStats, // indexedByOwnerID
    rankings,
    allOpportunities,
  };
}

function winLossReducer(action) {
  const {
    status, errors, opportunities, owners, accounts,
    hasNoDecision, users, products, quickSearch,
  } = action;

  
  const indexedProducts = products || {};
  const indexedAccounts = accounts || {};
  
  const now = moment().startOf('day');
  let totalExclude = 0;
  const ownerStats = {};
  const oneStats = {
    wonAmount: 0,
    wonCount: 0,
    wonIds: [],
    wonAmountRate: 0,
    wonCountRate: 0,

    lostAmount: 0,
    lostCount: 0,
    lostIds: [],
    lostAmountRate: 0,
    lostCountRate: 0,

    noDecisionAmount: 0,
    noDecisionCount: 0,
    noDecisionIds: [],
    noDecisionAmountRate: 0,
    noDecisionCountRate: 0,
  };
  const stats = cloneDeep(oneStats);

  each(accounts, (account) => {
    if (owners[account.owner_id]) {
      account.owner = owners[account.owner_id];
    }
    account.last_won_opportunity = null;
    account.won_count = 0;
    account.lost_count = 0;
    account.line_items = [];

    if (account.exclusion !== null && account.exclusion !== undefined) {
      account.excluded = true;
      account.exclusion = { user: users[account.exclusion.user__id], ...account.exclusion };
    }
  });

  each(opportunities, (opportunity) => {
    opportunity.quickSearch = quickSearch;
    const amount = opportunity.amount || 0;
    const ownerId = opportunity.owner_id;
    const opportunityId = opportunity.opportunity_id;
    const closeDate = opportunity.close_date ? moment(opportunity.close_date, 'YYYY-MM-DD') : null;
    const createdDate = opportunity.created_date ? moment(opportunity.created_date, 'YYYY-MM-DD') : null;
    opportunity.close_date = closeDate && closeDate.isValid() ? closeDate.startOf('day') : null;
    opportunity.account = indexedAccounts[opportunity.account_id];
    if (indexedAccounts[opportunity.account_id]) {
      indexedAccounts[opportunity.account_id].quickSearch = quickSearch;
    }
    
    opportunity.products = reduce(values(keyBy(opportunity.line_items, 'product_id')), (products, lineItem) => {
      if (indexedProducts[lineItem.product_id]) {
        products.push(indexedProducts[lineItem.product_id].name);
      }
      return products;
    }, []);

    let outcomeP = 1;
    if (opportunity.stage === 'Won') {
      outcomeP = 1000;
    } else if (opportunity.stage === 'No Decision') {
      outcomeP = 100;
    } else if (opportunity.stage === 'Lost') {
      outcomeP = 10;
    }
    opportunity.outcome = opportunity.close_date ? outcomeP * Number.parseInt(opportunity.close_date.format('x'), 10) : -1;

    // let outcome = opportunity.close_date ? opportunity.close_date.format
    if (opportunity.close_date) {
      const diff = now.diff(opportunity.close_date, 'days');
      let unit = 'day';
      if (diff > 30 && diff <= 365) {
        unit = 'month';
      } else if (diff > 365) {
        unit = 'year';
      }
      const unitDiff = now.diff(opportunity.close_date, unit);
      opportunity.close_date_ago = `${unitDiff} ${pluralize(unit, unitDiff)} ago`;
    }
    opportunity.created_date = createdDate;
    opportunity.owner = owners[ownerId];
    opportunity.account = accounts[opportunity.account_id];
    opportunity.excluded = false;
    if (opportunity.exclusion !== null && opportunity.exclusion !== undefined) {
      opportunity.excluded = true;
      opportunity.exclusion = { user: users[opportunity.exclusion.user__id], ...opportunity.exclusion };
      totalExclude += 1;
    }
    
    if (!ownerStats[ownerId]) {
      ownerStats[ownerId] = {
        owner: owners[ownerId],
        ...(cloneDeep(oneStats)),
      };
    }
    let stageKey = null;
    if (opportunity.stage === 'Won') {
      stageKey = 'won';
    } else if (opportunity.stage === 'Lost') {
      stageKey = 'lost';
    } else if (opportunity.stage === 'No Decision' && hasNoDecision) {
      stageKey = 'noDecision';
    }
    if (stageKey) {
      ownerStats[ownerId][`${stageKey}Amount`] += amount;
      ownerStats[ownerId][`${stageKey}Count`] += 1;
      ownerStats[ownerId][`${stageKey}Ids`].push(opportunityId);
      stats[`${stageKey}Amount`] += amount;
      stats[`${stageKey}Count`] += 1;
      stats[`${stageKey}Ids`].push(opportunityId);
      if (opportunity.account) {
        if (stageKey === 'won') {
          const lastWon = pick(opportunity, ['name', 'opportunity_id', 'close_date']);
          if (opportunity.account.last_won_opportunity) {
            if (opportunity.account.last_won_opportunity.close_date.isBefore(lastWon.close_date, 'day')) {
              opportunity.account.last_won_opportunity = lastWon;
            }
          } else {
            opportunity.account.last_won_opportunity = lastWon;
          }
          opportunity.account.won_count += 1;

          if (!opportunity.account.won_opportunities) {
            opportunity.account.won_opportunities = [opportunity.opportunity_id];
          }
          opportunity.account.won_opportunities.push();
        } else {
          opportunity.account.lost_count += 1;
        }
        // opportunity.account.line_items = concat(opportunity.account.line_items, opportunity.line_items);
      }
    }
  });
  each({ ...ownerStats, total: stats }, (ownerStat) => {
    const ownerAmount = ownerStat.wonAmount + ownerStat.lostAmount + ownerStat.noDecisionAmount;
    const ownerCount = ownerStat.wonCount + ownerStat.lostCount + ownerStat.noDecisionCount;
    ownerStat.wonAmountRate = ownerAmount !== 0 ? ownerStat.wonAmount / ownerAmount : 0;
    ownerStat.lostAmountRate = ownerAmount !== 0 ? ownerStat.lostAmount / ownerAmount : 0;
    ownerStat.noDecisionAmountRate = ownerAmount !== 0 ? ownerStat.noDecisionAmount / ownerAmount : 0;
    ownerStat.wonCountRate = ownerCount !== 0 ? ownerStat.wonCount / ownerCount : 0;
    ownerStat.lostCountRate = ownerCount !== 0 ? ownerStat.lostCount / ownerCount : 0;
    ownerStat.noDecisionCountRate = ownerCount !== 0 ? ownerStat.noDecisionCount / ownerCount : 0;
  });

  const rankings = reduce(['won', 'lost', 'noDecision'], (rks, stage) => {
    rks[stage] = reduce(ownerStats, (os, ownerStat, statOwnerId) => {
      os.push({
        owner: owners[statOwnerId],
        amount: ownerStat[`${stage}Amount`],
        count: ownerStat[`${stage}Count`],
      });
      return os;
    }, []);
    rks[stage] = orderBy(rks[stage], ['amount'], stage === 'won' ? ['desc'] : ['asc']);
    return rks;
  }, {});
  return {
    status,
    errors,
    opportunities,
    ownerStats,
    stats,
    rankings,
    totalExclude,
    accounts: values(accounts),
    editingOpportunity: null,
    editingAccount: null,
    feedbacks: {},
    surveys: {},
    internalEmails: {},
    reasonStats: {}, // reset
    accountJobs: {},
    accountResults: {},
    feedbackStatus: null,
    currentInsight: {
      status: null,
      errors: null,
      data: null,
      show: false,
    }, // reset
    currentEmail: {},
    indexedCollectorStats: {},
  };
}

function fillStageFilter(statsFilters, stageKey, indexedStatsFilters, opportunity) {
  const totalKey = '__ALL__';
  const stageName = opportunity.stage;

  each([totalKey, stageKey], (statsKey) => {
    if (!statsFilters[statsKey]) {
      const stageParams = {};
      if (statsKey !== totalKey) {
        stageParams.stage = stageName;
      }
      statsFilters[statsKey] = [
        {
          label: 'Total', key: 'total', count: 0, params: { ...stageParams },
        },
        {
          label: 'Need Feedback', key: 'needReasons', count: 0, params: { ...stageParams, need_reasons: true, has_requests: false },
        },
        {
          label: 'In Outreach', key: 'inOutreach', count: 0, params: { ...stageParams, in_outreach: true },
        },
        {
          label: 'Have Feedback', key: 'hasFeedbacks', count: 0, params: { ...stageParams, has_feedbacks: true },
        },
        {
          label: 'Refused', key: 'hasRefused', count: 0, params: { ...stageParams, has_refused: true },
        },
        {
          label: 'Non-Responsive', key: 'hasNonResponsive', count: 0, params: { ...stageParams, has_non_responsive: true },
        },
      ];
      each(statsFilters[statsKey], (sf) => {
        if (!indexedStatsFilters[statsKey]) {
          indexedStatsFilters[statsKey] = {};
        }
        indexedStatsFilters[statsKey][sf.key] = sf;
      });
    }
    indexedStatsFilters[statsKey].total.count += 1;
    if (opportunity.has_feedbacks) {
      indexedStatsFilters[statsKey].hasFeedbacks.count += 1;
    }
    if (opportunity.in_outreach) {
      indexedStatsFilters[statsKey].inOutreach.count += 1;
    }
    if (opportunity.need_reasons) {
      indexedStatsFilters[statsKey].needReasons.count += 1;
    }
    if (opportunity.has_refused) {
      indexedStatsFilters[statsKey].hasRefused.count += 1;
    }
    if (opportunity.has_non_responsive) {
      indexedStatsFilters[statsKey].hasNonResponsive.count += 1;
    }
  });
}

function getPricePositions(feedbacks) {
  const a = orderBy(
    filter(feedbacks, { is_client: false, is_strongest: true }),
    ['impact', 'label', i => (i.price_position ? i.price_position.value : 0)],
    ['asc', 'asc', 'asc'],
  );
  const p = {};
  each(a, (i) => {
    if (i.price_position && i.price_position.label !== 'NA') {
      const { label } = i.price_position;
      if (!p[label]) {
        p[label] = {
          label, contacts: [],
        };
      }
      p[label].contacts.push(i.contact_name);
    }
  });
  return values(p);
}

function attachStatus(o) {
  o.feedback_status_label = null;
  o.feedback_status_value = 10000000;
  if (o.excluded) {
    // Excluded should come first, because it won't be calculated
    o.feedback_status_value = 100000;
    o.feedback_status_label = 'Excluded';
  } else if (o.has_feedbacks) {
    // has feedback
    o.feedback_status_value = 10;
    o.feedback_status_label = 'Has Feedback';
  } else if (o.in_outreach) {
    // requesting
    o.feedback_status_value = 100;
    o.feedback_status_label = 'Requesting';
  } else if (o.has_non_responsive) {
    // No response
    o.feedback_status_value = 1000;
    o.feedback_status_label = 'No response';
  } else if (o.requested_no_outreach) {
    // Submitted
    o.feedback_status_value = 10000;
    o.feedback_status_label = 'Submitted';
  } else if (o.no_request_no_feedback) {
    // No Requests, no feedback
    o.feedback_status_value = 1000000;
    o.feedback_status_label = 'No Requests';
  }
}

function winLossReasonStatsReducer(reasons, collectorStats, inputOpportunities, inputIndexedCollectorStats) {
  const reasonStats = {};
  const unknown = '__UNKNOWN__'; // just make it special key
  const knownCount = {}; // per stage
  const statsFilters = {};
  const indexedStatsFilters = {};

  const indexedCollectorStats = {
    ...inputIndexedCollectorStats,
    ...collectorStats,
  };

  const now = moment();
  const opportunities = map(inputOpportunities, (opportunity) => {
    const o = { ...opportunity };
    const amount = o.amount || 0;
    const opportunityId = opportunity.opportunity_id;
    // o.close_date is already converted to momment
    const closeDate = o.close_date ? o.close_date.unix() : now.unix();
    const factor = 100;
    o.max_feedback_date = closeDate * 1; // default
    o.min_feedback_date = closeDate * factor; // default


    o.max_request_date = closeDate * 1; // default
    o.min_request_date = closeDate * factor; // default
    if (reasons[opportunityId]) {
      const result = reasons[opportunityId] || {};

      o.tv__opportunity_id = result.tv__opportunity_id;
      o.publish_date = result.publish_date ? moment(result.publish_date).toDate() : null; // most recent interview
      o.overview_url = result.overview_url;

      o.feedbacks = result.outcome_reasons || [];
      o.strongest_competitors = orderBy(result.strongest_competitors || [], [rs => moment(rs.created).unix()], ['desc']);
      o.tv_outcome = null;

      // price positions
      o.price_positions = orderBy(result.price_positions || [], [rs => moment(rs.created).unix()], ['desc']);

      // price_positions has battlecard_url
      const indexedPricePosition = keyBy(o.price_positions, p => `${p.contact_id}|${p.vendor_name}`);
      each(o.feedbacks, (f) => {
        if (f.price_position) {
          if (indexedPricePosition[`${f.contact_id}|${f.price_position.vendor_name}`]) {
            f.price_position.battlecard_url = indexedPricePosition[`${f.contact_id}|${f.price_position.vendor_name}`].battlecard_url;
          }
        }
        if (f.outcome) {
          o.tv_outcome = f.outcome;
        }
      });



      /*
      technically, each `result.outcome_reasons` should have a date, because there may be more than 1 contacts gave feedback for this oppotunity
      */
      if (o.publish_date && o.feedbacks.length) {
        // for some reason, TV API return feedbacks = [], but has publish_date
        o.max_feedback_date = moment(o.publish_date).unix() * factor;
        o.min_feedback_date = moment(o.publish_date).unix() * 1;
      }

      if (o.publish_date) {
        const existentContactIds = map(o.feedbacks, 'contact_id')
        if (o.price_positions.length > 0) {
          const indexedCompetitors = keyBy(result.strongest_competitors, 'contact_id');
          map(o.price_positions, (pricePosition) => {
            if (indexOf(existentContactIds, pricePosition.contact_id) < 0) {
              o.feedbacks.push({
                ...pricePosition,
                has_no_impacts: true,
                price_position: pricePosition,
                strongest_competitors: indexedCompetitors[pricePosition.contact_id] ? indexedCompetitors[pricePosition.contact_id] : null,
                publish_date: o.publish_date,
              });
            }
          });

        } else if (result.strongest_competitors && result.strongest_competitors.length) {
          map(result.strongest_competitors, (competitor) => {
            if (indexOf(existentContactIds, competitor.contact_id) < 0) {
              o.feedbacks.push({
                ...competitor,
                has_no_impacts: true,
                has_no_price_position: true,
                publish_date: o.publish_date,
              });
            }
          });
        }
      }


      /*
      const a = filter(map(groupBy(o.feedbacks, 'contact_id'), (fbs) => {
        return fbs.length;
      }), (b) => { return b > 1; });

      if (a.length > 1) {
        console.log('oo', o);
        alert(o.opportunity_id);
      }
      */
    }

    if (collectorStats[opportunityId]) {
      indexedCollectorStats[opportunityId] = collectorStats[opportunityId];
      // o.collectorStats = collectorStats[opportunityId] || {};
    }
    if (!o.feedbacks) {
      o.feedbacks = [];
    }
    if (!o.strongest_competitors) {
      o.strongest_competitors = [];
    }
    if (!o.price_positions) {
      o.price_positions = [];
    }
    if (!indexedCollectorStats[opportunityId]) {
      indexedCollectorStats[opportunityId] = {};
    }


    const stage = camelCase(o.stage);
    if (!reasonStats[stage]) {
      reasonStats[stage] = {
        [unknown]: {
          label: 'Unknown',
          id: 'unknown',
          isUnknown: true,
          opportunityIds: [],
          count: 0,
          majorCount: 0,
          minorCount: 0,
          amount: 0,
        },
      };
    }
    if (knownCount[stage] === undefined) {
      knownCount[stage] = 0;
    }
    const clientFeedbacks = filter(o.feedbacks, { is_client: true });

    if (clientFeedbacks.length === 0) {
      reasonStats[stage][unknown].count += 1;
      reasonStats[stage][unknown].amount += amount;
      reasonStats[stage][unknown].opportunityIds.push(opportunityId);
    } else {
      // every reason should be count once PER opportunity! unique by label!!!!
      knownCount[stage] += 1; // opportunities with at least on reasons
      const uniqueReasons = keyBy(clientFeedbacks, 'id');
      each(uniqueReasons, (reason) => {
        const k = reason.label; // by label!!!! not id!
        if (!reasonStats[stage][k]) {
          reasonStats[stage][k] = {
            label: reason.label,
            id: camelCase(reason.label),
            count: 0,
            majorCount: 0,
            minorCount: 0,
            isUnknown: false,
            amount: 0,
            majorFrequency: 0,
            frequency: 0,
            opportunityIds: [],
          };
        }

        if (reason.impact === 1) {
          reasonStats[stage][k].majorCount += 1;
        } else {
          reasonStats[stage][k].minorCount += 1;
        }
        reasonStats[stage][k].count += 1;
        reasonStats[stage][k].amount += amount;
        reasonStats[stage][k].opportunityIds.push(opportunityId);
      });
    }
    /*
    NEED_REASONS:
      The opportunity does not have any completed win loss surveys/interviews, AND
      The opportunity does not have any contacts in the outreach phase,

    IN_OUTREACH:
      have at least one contact in the outreach process and has not been terminated

    HAVE_FEEDBACKS:
      have at least one contact that has completed a survey and provided outcome reasons

    REFUSED:
      Have at least one contact that refused to complete the survey

    NON_RESPONSIVE:
      have at least one contact that was finished the outreach process, but was non-responsive to any of the outreach emails

    HAVE_REQUESTS:
      Have at least one contact that requesting
    */
    const opportunityCollectorStats = indexedCollectorStats[opportunityId];
    o.has_feedbacks = clientFeedbacks.length > 0 || filter(o.feedbacks, fb => fb.has_no_impacts === true).length > 0;



    let tvStage = o.stage;
    if (o.has_feedbacks) {
      const firstFeedback = first(clientFeedbacks);
      if (firstFeedback) {
        if (firstFeedback.outcome === 1) {
          tvStage = 'Won';
        } else if (firstFeedback.outcome === 2) {
          tvStage = 'Lost';
        } else if (firstFeedback.outcome === 1) {
          tvStage = 'No Decision';
        }
      }
    }
    o.tv_stage = tvStage;
    o.requested_no_outreach = opportunityCollectorStats.total > 0 && opportunityCollectorStats.inOutreach === 0;
    o.in_outreach = opportunityCollectorStats.inOutreach > 0;
    o.has_requests = opportunityCollectorStats.total > 0;
    o.need_reasons = clientFeedbacks.length === 0 && opportunityCollectorStats.inOutreach === 0;
    o.no_request_no_feedback = o.need_reasons && !o.has_requests;

    o.has_refused = opportunityCollectorStats.refused > 0;
    o.has_non_responsive = opportunityCollectorStats.nonResponsive > 0;
    o.has_bounced = opportunityCollectorStats.bounced > 0;

    attachStatus(o);

    if (opportunityCollectorStats && opportunityCollectorStats.collectors && opportunityCollectorStats.collectors.length) {
      // multiple collector
      // find the min and max across these multiple collector
      let currentMin = null;
      let currentMax = null;
      each(opportunityCollectorStats.collectors, (collector) => {
        const cMin = collector.first_request_date ? moment(collector.first_request_date).unix() : null;
        const cMax = collector.last_request_date ? moment(collector.last_request_date).unix() : null;
        if (cMin) {
          if (!currentMin) {
            currentMin = cMin;
          } else {
            currentMin = Math.min(cMin, currentMin);
          }
        }
        if (cMax) {
          if (!currentMax) {
            currentMax = cMax;
          } else {
            currentMax = Math.max(cMax, currentMax);
          }
        }
      });
      if (currentMin) {
        o.min_request_date = currentMin * 1;
      }
      if (currentMax) {
        o.max_request_date = currentMax * factor;
      }
    }

    fillStageFilter(statsFilters, stage, indexedStatsFilters, o);

    return o;
  });

  each(reasonStats, (stageReasons, stage) => {
    each(stageReasons, (reason) => {
      const kc = knownCount[stage] || 0;
      if (!reason.isUnknown) {
        reason.frequency = kc !== 0 ? reason.count / kc : 0;
        reason.majorFrequency = kc !== 0 ? reason.majorCount / kc : 0;
      }
    });
  });
  /*
  console.log('=======');

  const table = new CliTable({
    head: ['Reason', 'Major Count', 'Major Freq', 'Minor Count', 'count', 'frequency'],
  });
  const boo = orderBy(reasonStats['won'], ['frequency', 'majorCount'], ['desc', 'desc']);
  each(boo, (r) => {
    table.push([
      r.label,
      r.majorCount,
      numeral(r.majorFrequency).format('0.0%'),
      r.minorCount,
      r.count,
      numeral(r.frequency).format('0.0%'),
    ]);
  });
  console.log(table.toString());
  console.log('---known count', knownCount['won']);
  */

  const topThreeReasonStats = reduce(reasonStats, (items, sReasons, stage) => {
    // const topThree = slice(orderBy(filter(values(sReasons), { isUnknown: false }), ['frequency', 'majorCount'], ['desc', 'desc']), 0, 3);
    items[stage] = orderBy(values(sReasons), ['isUnknown', 'frequency', 'majorCount', 'label'], ['asc', 'desc', 'desc', 'asc']);
    items[stage] = each(items[stage], (i, idx) => {
      i.order = i.isUnknown ? null : idx + 1;
    });
    return items;
  }, {});
  return {
    opportunities,
    reasonStats: topThreeReasonStats,
    feedbackStatus: 'loaded',
    statsFilters,
    indexedCollectorStats,
  };
}

function winLossAccountReasonStatsReducer(reasons, collectorStats, inputAccounts, inputIndexedCollectorStats) {

  const indexedCollectorStats = {
    ...inputIndexedCollectorStats,
  };
  const now = moment();
  const closeDate = now.unix();
  const accounts = map(inputAccounts, (account) => {
    const o = { ...account };
    const id = account.account_id;
    const aid = `account-${account.account_id}`;
    const factor = 100;

    o.max_feedback_date = closeDate * 1; // default
    o.min_feedback_date = closeDate * factor; // default


    o.max_request_date = closeDate * 1; // default
    o.min_request_date = closeDate * factor; // default
    o.feedbacks = [];
    o.old_feedbacks = []; // more than 7 months
    o.eighteen_month_feedbacks = []; // less than 18 months
    if (reasons[aid]) {
      const result = reasons[aid] || {};
      const fbs = reduce(result.outcome_reasons, (items, r) => {
        // if (r.is_client === true && r.reasearch_type === 'Buyer Decision') {
        if (r.is_client === true) {
          items.push({
            ...r,
            publish_date: result.publish_date,
          });
        }
        return items;
      }, []);
      const clientFeedbacks = orderBy(fbs, [rs => moment(rs.created).unix()], ['desc']);

      const publishDate = result.publish_date ? moment(result.publish_date).toDate() : null;
      if (publishDate) {
        if (now.diff(publishDate, 'months') <= 7) {
          o.feedbacks = clientFeedbacks;
        } else {
          o.old_feedbacks = clientFeedbacks;
        }
        if (now.diff(publishDate, 'months') <= 18) {
          o.eighteen_month_feedbacks = clientFeedbacks;
        }
      }

      // technically, each `result.outcome_reasons` should have a date, because there may be more than 1 contacts gave feedback for this oppotunity
      if (result.publish_date && o.feedbacks.length) {
        o.tv__opportunity_id = result.tv__opportunity_id;
        o.publish_date = result.publish_date ? moment(result.publish_date).toDate() : null; // most recent interview
        o.overview_url = result.overview_url;
        // for some reason, TV API return feedbacks = [], but has publish_date
        o.max_feedback_date = moment(o.publish_date).unix() * factor;
        o.min_feedback_date = moment(o.publish_date).unix() * 1;
      }
    }

    if (collectorStats[aid]) {
      indexedCollectorStats[id] = collectorStats[aid];
    }

    o.excluded = false;
    if (o.exclusion !== null && o.exclusion !== undefined) {
      o.excluded = true;
      // opportunity.exclusion = { user: users[opportunity.exclusion.user__id], ...opportunity.exclusion };
      // totalExclude += 1;
    }

    if (!o.feedbacks) {
      o.feedbacks = [];
    }
    if (!indexedCollectorStats[id]) {
      indexedCollectorStats[id] = {};
    }

    // price positions
    o.price_positions = getPricePositions(o.feedbacks);

    /*
    NEED_REASONS:
      The opportunity does not have any completed win loss surveys/interviews, AND
      The opportunity does not have any contacts in the outreach phase,

    IN_OUTREACH:
      have at least one contact in the outreach process and has not been terminated

    HAVE_FEEDBACKS:
      have at least one contact that has completed a survey and provided outcome reasons

    REFUSED:
      Have at least one contact that refused to complete the survey

    NON_RESPONSIVE:
      have at least one contact that was finished the outreach process, but was non-responsive to any of the outreach emails
    */
    const accountCollectorStats = indexedCollectorStats[id];

    o.has_feedbacks = o.feedbacks.length > 0;
    o.in_outreach = accountCollectorStats.inOutreach > 0;
    o.has_requests = accountCollectorStats.total > 0;
    o.need_reasons = o.feedbacks.length === 0 && accountCollectorStats.inOutreach === 0;
    o.has_refused = accountCollectorStats.refused > 0;
    o.has_non_responsive = accountCollectorStats.nonResponsive > 0;
    o.has_bounced = accountCollectorStats.bounced > 0;
    o.requested_no_outreach = accountCollectorStats.total > 0 && accountCollectorStats.inOutreach === 0;
    o.no_request_no_feedback = o.need_reasons && !o.has_requests;

    attachStatus(o);

    if (accountCollectorStats && accountCollectorStats.collectors && accountCollectorStats.collectors.length) {
      // multiple collector
      // find the min and max across these multiple collector
      let currentMin = null;
      let currentMax = null;
      each(accountCollectorStats.collectors, (collector) => {
        const cMin = collector.first_request_date ? moment(collector.first_request_date).unix() : null;
        const cMax = collector.last_request_date ? moment(collector.last_request_date).unix() : null;
        if (cMin) {
          if (!currentMin) {
            currentMin = cMin;
          } else {
            currentMin = Math.min(cMin, currentMin);
          }
        }
        if (cMax) {
          if (!currentMax) {
            currentMax = cMax;
          } else {
            currentMax = Math.max(cMax, currentMax);
          }
        }
      });
      if (currentMin) {
        o.min_request_date = currentMin * 1;
      }
      if (currentMax) {
        o.max_request_date = currentMax * factor;
      }
    }
    return o;
  });
  return {
    accounts,
    feedbackStatus: 'loaded',
    indexedCollectorStats,
  };
}


const reasonReducer = (opportunities, exclusionReasons = []) => {
  const indexedReasons = keyBy(exclusionReasons, '_id') || {};
  const stats = {
    Won: {
      count: 0,
      amount: 0,
      percentageAmount: 0,
      percentageCount: 0,
      opportunityIds: [],
      reasons: [],
    },
    Lost: {
      count: 0,
      amount: 0,
      reasons: [],
      opportunityIds: [],
      percentageAmount: 0,
      percentageCount: 0,
    },
    'No Decision': {
      count: 0,
      amount: 0,
      reasons: [],
      opportunityIds: [],
      percentageAmount: 0,
      percentageCount: 0,
    },
    Open: {
      count: 0,
      amount: 0,
      reasons: [],
      opportunityIds: [],
      percentageAmount: 0,
      percentageCount: 0,
    },
  };
  const validStages = ['Won', 'Lost', 'No Decision'];

  const otherReason = find(exclusionReasons, { type: 'OTHER' }) || {
    _id: 'OTHER',
    type: 'OTHER',
    label: 'Other',
  };

  const contactExcludedReason = {
    _id: 'CONTACT',
    type: 'CONTACT',
    label: 'All contacts was excluded',
  };

  let totalAmount = 0;
  const totalCount = opportunities.length;

  each(opportunities, (opportunity) => {
    let reason = otherReason;
    if (opportunity.exclusion) {
      reason = indexedReasons[opportunity.exclusion.reason_id] || otherReason;
      if (reason.type !== 'OTHER') {
        opportunity.exclusion.reason = reason.label;
      }
      if (!opportunity.exclusion.reason) {
        opportunity.exclusion.reason = 'Unknown';
      }
      if (reason && !opportunity.exclusion.type) {
        opportunity.exclusion.type = reason.type;
      }
    } else {
      reason = contactExcludedReason;
      opportunity.exclusion = contactExcludedReason;
      opportunity.exclusion.reason = contactExcludedReason.label;
    }


    let stageKey = opportunity.stage;
    if (indexOf(validStages, stageKey) < 0) {
      stageKey = 'Open';
    }

    const stageStats = stats[stageKey] || {};


    if (!stageStats.reasons[reason._id]) {
      stageStats.reasons[reason._id] = {
        ...pick(reason, ['_id', 'label', 'type', 'is_system', 'sort_order', 'description']),
        opportunityIds: [],
        amount: 0,
        count: 0,
      };
    }
    

    const amount = Math.max(opportunity.amount, 0) || 0;


    totalAmount += amount;

    stageStats.amount += amount
    stageStats.count += 1;
    stageStats.opportunityIds.push(opportunity.opportunity_id);

    stageStats.reasons[reason._id].amount += amount;
    stageStats.reasons[reason._id].count += 1;
    stageStats.reasons[reason._id].opportunityIds.push(opportunity.opportunity_id);

  });

  // clean up
  each(stats, (stateStats, k) => {
    stats[k].reasons = orderBy(values(stateStats.reasons), ['amount'], ['desc']);
    each(stats[k].reasons, (r, i) => {
      r.order = i + 1;
    });
    const other = find(stats[k].reasons, { type: 'OTHER' });
    if (stats[k].reasons.length > 0 && !other) {
      stats[k].reasons.push({
        ...otherReason,
        order: stats[k].reasons.length + 1,
        reasons: [],
        amount: 0,
        count: 0,
      });
    }
    stateStats.percentageAmount = totalAmount !== 0 ? stateStats.amount / totalAmount : 0;
    stateStats.percentageCount = totalCount !== 0 ? stateStats.count / totalCount : 0;
  });



  return stats;
}


const exclusionReducer = (action) => {
  const { opportunities } = action;
  const indexedOwners = action.owners || {};
  const indexedAccounts = action.accounts || {};
  const indexedProducts = action.products || {};
  
  each(opportunities, (opportunity) => {
    opportunity.owner = indexedOwners[opportunity.owner_id];
    opportunity.account = indexedAccounts[opportunity.account_id];
    const closeDate = opportunity.close_date ? moment(opportunity.close_date, 'YYYY-MM-DD') : null;
    const createdDate = opportunity.created_date ? moment(opportunity.created_date, 'YYYY-MM-DD') : null;
    opportunity.close_date = closeDate;
    opportunity.created_date = createdDate;
    opportunity.products = reduce(values(keyBy(opportunity.line_items, 'product_id')), (products, lineItem) => {
      if (indexedProducts[lineItem.product_id]) {
        products.push(indexedProducts[lineItem.product_id].name);
      }
      return products;
    }, []);
  });


  return {
    ...action,
    opportunities,
  };
}


const excludeOpportunityReducer = (opportunities, result) => {
  const idx = findIndex(opportunities, { opportunity_id: result.opportunity_id });
  if (idx >= 0) {
    const newOpportunities = [...opportunities];
    const oppotunity = { ...newOpportunities[idx] };
    oppotunity.excluded = true;
    oppotunity.exclusion = {
      user__id: result.user__id,
      reason_id: result.reason_id,
      created_at: result.created_at,
      reason: result.reason,
      user: result.user,
    };
    newOpportunities[idx] = oppotunity;
    return newOpportunities;
  }
  return null;
}

const DashboardReducer = (state = initialState, action) => {
  switch (action.type) {
    case DASHBOARD_RESET_ALL: {
      const { resetTeams } = action;
      const { teams } = state;
      const newState = cloneDeep(emptyState);
      if (!resetTeams) {
        newState.teams = teams;
      }
      return newState;
    }
    case DASHBOARD_FETCH_PIPELINE_OPPORTUNITIES: {
      return {
        ...state,
        pipeline: {
          ...state.pipeline,
          ...pipelineReducer(action),
        },
      };
    }
    case DASHBOARD_FETCH_FORECAST_OPPORTUNITIES: {
      return {
        ...state,
        forecast: forecastReducer(action),
      };
    }
    case DASHBOARD_FETCH_CONVERSION_OPPORTUNITIES: {
      const { owners } = action;

      const conversion = conversionGraphReducer(action, state.pipeline.stages, state.conversion);

      const projectedSales = calculateProjectedSales(
        filter(conversion.average, { skip_conversion: false }),
        state.pipeline.stages, // array of stages
      );
      const ownerProjectedSales = reduce(state.pipeline.ownerStages, (items, stages, ownerId) => {
        const owner = conversion.owners[ownerId];
        if (owner && owner.stages) {
          items[ownerId] = calculateProjectedSales(
            filter(owner.stages, { skip_conversion: false }),
            stages, // array of stages
            false,
          );
        }
        return items;
      }, {});

      const rankings = orderBy(reduce(ownerProjectedSales, (items, stats, ownerId) => items.concat({
        owner: owners[ownerId],
        amount: stats.totalProjectedSales,
        count: stats.countTotalProjectedSales,
      }), []), ['amount'], ['desc']);

      return {
        ...state,
        conversion: {
          ...conversion,
          projectedSales,
          ownerProjectedSales,
          rankings,
        },
      };
    }
    case DASHBOARD_FETCH_CURRENT_GLOBAL_RANKINGS: {
      const { owners, status, errors } = action;
      return {
        ...state,
        rankings: {
          owners, status, errors,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK: {
      const { total, id } = action;
      const { winLoss } = state;
      const opportunities = reduce(winLoss.opportunities, (items, opportunity) => {
        if (opportunity.opportunity_id === id) {
          items.push({
            ...opportunity,
            requested_feedbacks: total,
          });
        } else {
          items.push(opportunity);
        }
        return items;
      }, []);
      const editingOpportunity = winLoss.editingOpportunity && winLoss.editingOpportunity.opportunity_id === id ? {
        ...winLoss.editingOpportunity,
        requested_feedbacks: total,
      } : winLoss.editingOpportunity;

      const pipelineOpportunities = reduce(state.pipeline.opportunities, (items, opportunity) => {
        if (opportunity.opportunity_id === id) {
          items.push({
            ...opportunity,
            requested_feedbacks: total,
          });
        } else {
          items.push(opportunity);
        }
        return items;
      }, []);


      return {
        ...state,
        winLoss: {
          ...winLoss,
          opportunities,
          editingOpportunity,
        },
        pipeline: {
          ...state.pipeline,
          opportunities: pipelineOpportunities,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_GLOBAL_RANKINGS: {
      const { owners, status, errors } = action;
      return {
        ...state,
        winLossRankings: {
          owners, status, errors,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITIES: {
      const { isApex, quickSearch } = action;
      const winLoss = action.opportunities ? winLossReducer(action) : pick(action, ['status', 'errors', 'hasNoDecision']);
      if (isApex) {
        delete winLoss.indexedCollectorStats;
      }
      
      
      if (quickSearch) {
        // we need to merge
        const currentWinLoss = { ...state.winLoss };
       
        const newOpportunities = filter(currentWinLoss.opportunities, { quickSearch: false }); // remove previous search
        const indexedOpportunities = keyBy(newOpportunities, 'opportunity_id');
        each(winLoss.opportunities, (opp) => {
          // `opp` is the MATCHED opp from backend
          if (!indexedOpportunities[opp.opportunity_id]) {
            // not found the matched opp in current result, we will add it to the current result
            newOpportunities.push(opp);
          } else {
            // we found it in the current results
            indexedOpportunities[opp.opportunity_id].quickSearchString = opp.quickSearchString;
          }
        });

        const newAccounts = filter(currentWinLoss.accounts, { quickSearch: false }); // remove previous search
        const indexedAccounts = keyBy(newAccounts, 'account_id');
        each(winLoss.accounts, (acc) => {
          if (!indexedAccounts[acc.account_id]) {
            newAccounts.push(acc);
          } else {
            // already in the current search
            indexedAccounts[acc.account_id].quickSearchString = acc.quickSearchString;
          }
        });


        const attrs = ['feedbacks', 'surveys', 'internalEmails', 'accountJobs', 'accountResults' , 'currentEmail', 'indexedCollectorStats'];
        each(attrs, (k) => {
          winLoss[k] = {
            ...winLoss[k],
            ...currentWinLoss[k],
          };
        });

        return {
          ...state,
          winLoss: {
            ...state.winLoss,
            ...pick(winLoss, attrs),
            opportunities: newOpportunities,
            accounts: newAccounts,
          },
        };
      }
      return {
        ...state,
        winLoss: {
          ...state.winLoss,
          ...winLoss,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_FEEDBACKS: {
      const { feedbacks, collectors, accountBase } = action;
      const { winLoss } = state;
      if (accountBase) {
        const {
          accounts, feedbackStatus, indexedCollectorStats,
        } = winLossAccountReasonStatsReducer(feedbacks, collectors, winLoss.accounts, winLoss.indexedCollectorStats);
        return {
          ...state,
          winLoss: {
            ...winLoss,
            accounts,
            feedbackStatus,
            indexedCollectorStats,
          },
        };
      }
      const {
        reasonStats, opportunities, feedbackStatus,
        statsFilters, indexedCollectorStats,
      } = winLossReasonStatsReducer(feedbacks, collectors, winLoss.opportunities, winLoss.indexedCollectorStats);

      return {
        ...state,
        winLoss: {
          ...winLoss,
          opportunities,
          reasonStats,
          feedbackStatus,
          statsFilters,
          indexedCollectorStats,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_SELLER_FEEDBACKS: {
      const { feedbacks } = action;
      const { winLoss } = state;
      const editingOpportunity = winLoss.editingOpportunity ? { ...winLoss.editingOpportunity } : null;

      const opportunities = reduce(winLoss.opportunities, (items, opportunity) => {
        const sellerFeedbacks = feedbacks[opportunity.opportunity_id];
        if (sellerFeedbacks) {
          const currentReasons = opportunity.seller_feedbacks || [];
          each(sellerFeedbacks, (reason) => {
            currentReasons.push(reason);
          });
          items.push({
            ...opportunity,
            seller_feedbacks: currentReasons,
            has_seller_feedback: true,
          });
          if (editingOpportunity && editingOpportunity.opportunity_id === opportunity.opportunity_id) {
            editingOpportunity.seller_feedbacks = currentReasons;
            editingOpportunity.has_seller_feedback = true;
          }
        } else {
          if (!opportunity.has_seller_feedback) {
            opportunity.has_seller_feedback = false;
          }
          items.push(opportunity);
        }
        return items;
      }, []);
      return {
        ...state,
        winLoss: {
          ...winLoss,
          opportunities,
          sellerFeedbacks: feedbacks,
          editingOpportunity,
        },
      };
    }
    case DASHBOARD_WIN_LOSS_EXCLUDE_OPPORTUNITY: {
      const { result } = action;
      const { winLoss } = state;
      let { totalExclude } = winLoss;

      const newWinLossOpportunities = excludeOpportunityReducer(winLoss.opportunities, result);
      const newExcludedOpportunities = excludeOpportunityReducer(state.exclusions.opportunities, result);
      let newState = state;

      if (newWinLossOpportunities) {
        // can be null
        totalExclude += 1;
        newState = {
          ...newState,
          winLoss: {
            ...winLoss,
            totalExclude,
            opportunities: newWinLossOpportunities,
          },
        };
      }

      if (newExcludedOpportunities) {
        // can be null
        newState = {
          ...newState,
          exclusions: {
            ...state.exclusions,
            opportunities: newExcludedOpportunities,
            stats: reasonReducer(newExcludedOpportunities, state.settings?.exclusion_reasons || []),
          },
        };
      }

      return newState;
    }
    case DASHBOARD_WIN_LOSS_EXCLUDE_ACCOUNT: {
      const { result } = action;
      const { winLoss } = state;
      const { accounts } = winLoss;
      let { totalExclude } = winLoss;
      const idx = findIndex(accounts, { account_id: result.opportunity_id });
      if (idx >= 0) {
        const newAccounts = [...accounts];
        const account = { ...newAccounts[idx] };
        totalExclude += 1;
        account.excluded = true;
        account.exclusion = {
          user__id: result.user__id,
          created_at: result.created_at,
          reason: result.reason,
          user: result.user,
        };
        newAccounts[idx] = account;
        return {
          ...state,
          winLoss: {
            ...winLoss,
            totalExclude,
            accounts: newAccounts,
          },
        };
      }
      return state;
    }
    case DASHBOARD_WIN_LOSS_INCLUDE_OPPORTUNITY: {
      const { opportunityId } = action;
      const { winLoss } = state;
      const { opportunities } = winLoss;
      // let { totalExclude } = winLoss;
      const idx = findIndex(opportunities, { opportunity_id: opportunityId });
      if (idx >= 0) {
        const newOpportunities = [...opportunities];
        const oppotunity = { ...newOpportunities[idx] };
        oppotunity.excluded = false;
        delete oppotunity.exclusion;
        newOpportunities[idx] = oppotunity;
        // totalExclude -= 1;
        return {
          ...state,
          winLoss: {
            ...winLoss,
            // totalExclude,
            opportunities: newOpportunities,
          },
        };
      }
      return state;
    }

    case DASHBOARD_WIN_LOSS_INCLUDE_ACCOUNT: {
      const { accountId } = action;
      const { winLoss } = state;
      const { accounts } = winLoss;
      // let { totalExclude } = winLoss;
      const idx = findIndex(accounts, { account_id: accountId });
      if (idx >= 0) {
        const newAccounts = [...accounts];
        const account = { ...newAccounts[idx] };
        account.excluded = false;
        delete account.exclusion;
        newAccounts[idx] = account;
        // totalExclude -= 1;
        return {
          ...state,
          winLoss: {
            ...winLoss,
            // totalExclude,
            accounts: newAccounts,
          },
        };
      }
      return state;
    }

    case DASHBOARD_REQUEST_EDIT_WIN_LOSS_OPPORTUNITY_FEEDBACKS: {
      const { opportunity, contact } = action;
      const { winLoss } = state;
      const { opportunities } = winLoss;
      let editingOpportunity = null;
      if (opportunity) {
        const requestOppotunity = find(opportunities, { opportunity_id: opportunity.opportunity_id });
        if (requestOppotunity) {
          editingOpportunity = cloneDeep(requestOppotunity);
        } else {
          editingOpportunity = cloneDeep(opportunity);
        }
        editingOpportunity.contact = contact || null;
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          editingOpportunity,
        },
      };
    }
    case DASHBOARD_REQUEST_EDIT_WIN_LOSS_ACCOUNT_FEEDBACKS: {
      const { account } = action;
      const { winLoss } = state;
      const { accounts } = winLoss;
      let editingAccount = null;
      if (account) {
        const requestAccount = find(accounts, { account_id: account.account_id });
        if (requestAccount) {
          editingAccount = cloneDeep(requestAccount);
        }
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          editingAccount,
        },
      };
    }
    case DASHBOARD_REQUEST_LOAD_WIN_LOSS_OPPORTUNITY_CONTACTS: {
      const { opportunityId, contacts } = action;
      const { winLoss } = state;
      const { editingOpportunity } = winLoss;
      let newEditingOpportunity = editingOpportunity;
      if (editingOpportunity && editingOpportunity.opportunity_id === opportunityId) {
        newEditingOpportunity = {
          ...editingOpportunity,
          contacts,
        };
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          editingOpportunity: newEditingOpportunity,
        },
      };
    }
    case DASHBOARD_REQUEST_LOAD_WIN_LOSS_ACCOUNT_CONTACTS: {
      const { accountId, contacts } = action;
      const { winLoss } = state;
      const { editingAccount } = winLoss;
      let newEditingAccount = editingAccount;
      if (editingAccount && editingAccount.account_id === accountId) {
        newEditingAccount = {
          ...editingAccount,
          contacts,
        };
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          editingAccount: newEditingAccount,
        },
      };
    }
    case ADD_OPPORTUNITY_CONTACT: {
      const { opportunityId, contact } = action;
      const { winLoss } = state;
      const { editingOpportunity } = winLoss;
      let newEditingOpportunity = editingOpportunity;
      if (editingOpportunity && editingOpportunity.opportunity_id === opportunityId) {
        newEditingOpportunity = {
          ...editingOpportunity,
        };
        const isPrimary = contact && contact.role && contact.role.is_primary;
        if (isPrimary) {
          // reset all
          newEditingOpportunity.contacts = reduce(newEditingOpportunity.contacts, (items, c) => {
            const newContact = { ...c };
            if (newContact.role) {
              newContact.role.is_primary = false;
            }
            items.push(newContact);
            return items;
          }, []);
        } else {
          newEditingOpportunity.contacts = [...newEditingOpportunity.contacts];
        }
        newEditingOpportunity.contacts.push(contact);
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          editingOpportunity: newEditingOpportunity,
        },
      };
    }
    case ADD_ACCOUNT_CONTACT: {
      const { accountId, contact } = action;
      const { winLoss } = state;
      const { editingAccount } = winLoss;
      let newEditingAccount = editingAccount;
      if (editingAccount && editingAccount.account_id === accountId) {
        newEditingAccount = {
          ...editingAccount,
        };
        newEditingAccount.contacts = [...newEditingAccount.contacts];
        newEditingAccount.contacts.push(contact);
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          editingAccount: newEditingAccount,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_FEEDBACK: {
      const { id, feedbacks } = action;
      const { winLoss } = state;
      return {
        ...state,
        winLoss: {
          ...winLoss,
          feedbacks: {
            ...winLoss.feedbacks,
            [id]: feedbacks,
          },
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_SURVEYS: {
      const { id, surveys } = action;
      const { winLoss } = state;
      return {
        ...state,
        winLoss: {
          ...winLoss,
          surveys: {
            ...winLoss.surveys,
            [id]: surveys,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_CANCEL_WIN_LOSS_SURVEY: {
      const { id, contactId, contact } = action;
      const { winLoss } = state;
      let currentOpportunitySurveys = null;
      if (winLoss.surveys[id]) {
        currentOpportunitySurveys = reduce(winLoss.surveys[id], (items, c) => {
          if (c.id === contactId) {
            // contactId = tv internal id
            items.push({
              ...c,
              ...contact,
            });
          } else {
            items.push(c);
          }
          return items;
        }, []);
      }

      return {
        ...state,
        winLoss: {
          ...winLoss,
          surveys: {
            ...winLoss.surveys,
            [id]: currentOpportunitySurveys,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_DELETE_WIN_LOSS_SURVEY: {
      const { id, contactId } = action;
      const { winLoss } = state;
      let currentOpportunitySurveys = null;
      if (winLoss.surveys[id]) {
        currentOpportunitySurveys = reduce(winLoss.surveys[id], (items, c) => {
          if (c.id !== contactId) {
            items.push(c);
          }
          return items;
        }, []);
      }

      return {
        ...state,
        winLoss: {
          ...winLoss,
          surveys: {
            ...winLoss.surveys,
            [id]: currentOpportunitySurveys,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_TOGGLE_PAUSE_WIN_LOSS_SURVEY: {
      const { id, contactId, contact } = action;
      const { winLoss } = state;
      let currentOpportunitySurveys = null;
      if (winLoss.surveys[id]) {
        currentOpportunitySurveys = reduce(winLoss.surveys[id], (items, c) => {
          if (c.id === contactId) {
            // contactId = tv internal id
            items.push({
              ...c,
              ...contact,
            });
          } else {
            items.push(c);
          }
          return items;
        }, []);
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          surveys: {
            ...winLoss.surveys,
            [id]: currentOpportunitySurveys,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_RESCHEDULE_WIN_LOSS_SURVEY: {
      const {
        id, contactId, stepId, contact,
      } = action;
      const { winLoss } = state;
      let currentEmail = winLoss.currentEmail ? { ...winLoss.currentEmail } : null;

      let currentOpportunitySurveys = null;
      if (winLoss.surveys[id]) {
        currentOpportunitySurveys = reduce(winLoss.surveys[id], (items, c) => {
          if (c.id === contactId) {
            // contactId = tv internal id
            items.push({
              ...c,
              ...contact,
            });
            const scheduleEmails = contact.scheduled_emails || {};

            const nextEmail = scheduleEmails[stepId];
            const aParams = {};
            if (nextEmail) {
              aParams.subject = nextEmail.subject;
              aParams.message = nextEmail.body || nextEmail.message;
              aParams.date = nextEmail.date || contact.next_email_date;
            }
            currentEmail = {
              ...currentEmail,
              ...aParams,
            };
          } else {
            items.push(c);
          }
          return items;
        }, []);
      }

      const { currentCollectorSummary } = winLoss;
      let newCollectorSummary = currentCollectorSummary;

      if (stepId && currentCollectorSummary && currentCollectorSummary.length) {
        newCollectorSummary = reduce(currentCollectorSummary, (items, step) => {
          if (step.id === stepId) {

            const stepContacts = reduce(step.contacts, (contacts, c) => {
              if (c.contact_id === contact.contact_id) {
                const scheduleEmails = contact.scheduled_emails || {};
                const scheduleEmail = scheduleEmails[stepId];

                let date = c.next_email_date;
                let subject = c.next_scheduled_subject || c.email_subject;
                let message = c.next_scheduled_message || c.email_message;

                if (scheduleEmail) {
                  date = scheduleEmail.date || date;
                  subject = scheduleEmail.subject || subject;
                  message = scheduleEmail.body || message;
                }
                contacts.push({
                  ...c,
                  ...contact,
                  next_email_date: date,
                  next_scheduled_subject: subject,
                  next_scheduled_message: message,
                });
              } else {
                contacts.push(c);
              }
              return contacts;
            }, []);
            items.push({
              ...step,
              contacts: stepContacts,
            });
          } else {
            items.push(step);
          }
          return items;
        }, []);
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          currentEmail,
          currentCollectorSummary: newCollectorSummary,
          surveys: {
            ...winLoss.surveys,
            [id]: currentOpportunitySurveys,
          },
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_INTERNAL_EMAILS: {
      const { id, internalEmails, isAccount } = action;
      const { winLoss } = state;

      
      const { opportunities, accounts } = winLoss;
      const newOpportunities = isAccount ? opportunities : reduce(opportunities, (items, opportunity) => {
        if (opportunity.opportunity_id === id) {
          items.push({
            ...opportunity,
            internal_email_count: internalEmails.length,
          });
        } else {
          items.push(opportunity);
        }
        return items;
      }, []);
      const newAccounts = !isAccount ? accounts : reduce(accounts, (items, account) => {
        if (account.account_id === id) {
          items.push({
            ...account,
            internal_email_count: internalEmails.length,
          });
        } else {
          items.push(account);
        }
        return items;
      }, []);

      return {
        ...state,
        winLoss: {
          ...winLoss,
          opportunities: newOpportunities,
          accounts: newAccounts,
          internalEmails: {
            ...winLoss.internalEmails,
            [id]: internalEmails,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_WIN_LOSS_SEND_NEXT_EMAIL: {
      const {
        id, contactId, result, collectorTZ,
      } = action;
      const { winLoss } = state;
      let currentOpportunitySurveys = null;
      if (winLoss.surveys[id]) {
        currentOpportunitySurveys = reduce(winLoss.surveys[id], (items, contact) => {
          if (contact.id === contactId) {
            // contactId = tv internal id
            items.push({
              ...contact,
              ...result,
              collector_id: contact.collector_id,
              collector_name: contact.collector_name,
              time_zone: collectorTZ,
            });
          } else {
            items.push(contact);
          }
          return items;
        }, []);
      }

      return {
        ...state,
        winLoss: {
          ...winLoss,
          surveys: {
            ...winLoss.surveys,
            [id]: currentOpportunitySurveys,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_LOAD_OPPORTUNITY_INSIGHTS: {
      const {
        data, status, errors, show,
      } = action;
      const { winLoss } = state;
      return {
        ...state,
        winLoss: {
          ...winLoss,
          currentInsight: {
            status,
            errors,
            data,
            show,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_LOAD_ACCOUNT_INSIGHTS: {
      const {
        data, status, errors, show,
      } = action;
      const { winLoss } = state;
      return {
        ...state,
        winLoss: {
          ...winLoss,
          currentInsight: {
            status,
            errors,
            data,
            show,
          },
        },
      };
    }
    case DASHBOARD_REQUEST_SHOW_WIN_LOSS_SURVEY_EMAIL: {
      const { data } = action;
      const { winLoss } = state;
      return {
        ...state,
        winLoss: {
          ...winLoss,
          currentEmail: data,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_ACCOUNT_RESULTS: {
      const {
        status, error, results, id,
      } = action;
      const { winLoss } = state;
      const accountResults = { ...winLoss.accountResults };
      accountResults[id] = {
        status, error, results,
      };
      return {
        ...state,
        winLoss: {
          ...winLoss,
          accountResults,
        },
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_ACCOUNT_JOBS: {
      const {
        status, error, jobs, id,
      } = action;
      const { winLoss } = state;
      const accountJobs = { ...winLoss.accountJobs };
      accountJobs[id] = {
        status, error, jobs,
      };
      return {
        ...state,
        winLoss: {
          ...winLoss,
          accountJobs,
        },
      };
    }
    case DASHBOARD_ADD_WIN_LOSS_ACCOUNT_JOB: {
      const { job, id } = action;
      const { winLoss } = state;
      const accountJobs = { ...winLoss.accountJobs };
      if (!accountJobs[id]) {
        accountJobs[id] = {
          jobs: [],
        };
      }
      const i = findIndex(accountJobs[id].jobs, { _id: job._id });
      if (i >= 0) {
        accountJobs[id].jobs = [...accountJobs[id].jobs];
        accountJobs[id].jobs[i] = job;
      } else {
        accountJobs[id].jobs.push(job);
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          accountJobs,
        },
      };
    }
    case DASHBOARD_DELETE_WIN_LOSS_ACCOUNT_JOB: {
      const { jobId, id } = action;
      const { winLoss } = state;
      const accountJobs = { ...winLoss.accountJobs };
      if (!accountJobs[id]) {
        accountJobs[id] = {
          jobs: [],
        };
      }
      accountJobs[id].jobs = filter(accountJobs[id].jobs, job => job._id !== jobId);
      return {
        ...state,
        winLoss: {
          ...winLoss,
          accountJobs,
        },
      };
    }
    case DASHBOARD_UPDATE_WIN_LOSS_ACCOUNT_STATUS: {
      const { account } = action;
      const { winLoss } = state;
      let { totalExclude } = winLoss;

      const accounts = reduce(winLoss.accounts, (items, acc) => {
        if (acc.account_id === account.account_id) {
          totalExclude += 1; // just to force refresh
          items.push({
            ...acc,
            ...pick(account, ['customer_status', 'customer_status_editor__id', 'customer_status_updated_date']),
          });
        } else {
          items.push(acc);
        }
        return items;
      }, []);
      return {
        ...state,
        winLoss: {
          ...winLoss,
          accounts,
          totalExclude,
        },
      };
    }
    case DASHBOARD_FETCH_COLLECTOR_SUMMARY: {
      const { steps } = action;
      const { winLoss } = state;

      return {
        ...state,
        winLoss: {
          ...winLoss,
          currentCollectorSummary: steps,
        },
      };
    }
    case DASHBOARD_FETCH_COLLECTOR_STATS: {
      const { collectorId, data, status } = action;
      const { collectorStats } = state;

      return {
        ...state,
        collectorStats: {
          ...collectorStats,
          [collectorId]: {
            status,
            data,
          },
        },
      };
    }
    case DASHBOARD_FEEDBACK_REQUEST_SETTINGS: {
      const { settings } = action;
      return {
        ...state,
        settings,
      };
    }
    case DASHBOARD_REQUEST_CHANGE_CONTACT_SENDER: {
      const {
        contactId, id, contact,
      } = action;
      const { winLoss } = state;
      let currentOpportunitySurveys = null;
      if (winLoss.surveys[id]) {
        currentOpportunitySurveys = reduce(winLoss.surveys[id], (items, c) => {
          if (c.id === contactId) {
            const a = {
              ...c,
              ...contact,
            };
            items.push(a);
          } else {
            items.push(c);
          }
          return items;
        }, []);
      }
      return {
        ...state,
        winLoss: {
          ...winLoss,
          surveys: {
            ...winLoss.surveys,
            [id]: currentOpportunitySurveys,
          },
        },
      };
    }
    case DASHBOARD_FETCH_OPPORTUNITY_FEEDBACK_REQUEST_STATUS: {
      const { feedbackRequestables } = action;
      Object.keys(feedbackRequestables).forEach(id => {
        state.feedbackRequestStatus[id] = feedbackRequestables[id];
      });

      return { ...state };
    }
    case DASHBOARD_FETCH_EXCLUDED_OPPORTUNITIES: {
      const exclusions = {
        ...state.exclusions,
        ...exclusionReducer(action),
      };
      exclusions.stats = reasonReducer(exclusions.opportunities, state.settings?.exclusion_reasons || []);

      return {
        ...state,
        exclusions,
      };
    }
    case DASHBOARD_FETCH_EXCLUDED_CONTACTS: {
      const { status, errors } = action;

      const { opportunities } = state.exclusions;
      const indexedOpportunities = keyBy(opportunities, 'opportunity_id');
      const contacts = reduce(action.contacts, (items, contact) => {
        if (indexedOpportunities[contact.opportunity_id]) {
          items.push({
            ...contact,
            ...indexedOpportunities[contact.opportunity_id],
          });
        }
        return items;
      }, []);

      return {
        ...state,
        excludedContacts: {
          contacts,
          status,
          errors,
        },
      };
    }
    case DASHBOARD_FETCH_PROGRESS_CONTACTS: {
      const { status, errors, automationSource } = action;

      const { opportunities, accounts } = state.winLoss;
      const indexedResources = automationSource === 'account' ? keyBy(accounts, 'account_id') : keyBy(opportunities, 'opportunity_id');
      const k = automationSource === 'account' ? 'account_id' : 'opportunity_id';

      const contacts = reduce(action.contacts, (items, contact) => {
        if (indexedResources[contact[k]]) {
          items.push({
            ...contact,
            ...indexedResources[contact[k]],
          });
        }
        return items;
      }, []);
      return {
        ...state,
        progressContacts: {
          contacts,
          status,
          errors,
        },
      };
    }
    case DASHBOARD_REQUEST_CHANGE_ACCOUNT_SIZE: {
      const { minAccountSize, maxAccountSize } = action;
      const { progressStats } = state.winLoss;
      each(progressStats, (stats) => {
        each(['small', 'medium', 'large'], (attr) => {
          if (stats[attr]) {
            stats[attr] = { count: 0, ids: [] };
          }
        });
      });
      const resources = state.winLoss.accounts;
      each(resources, (resource) => {
        const id = resource.account_id;
        const accountSize = resource.annual_revenue;
        let accountSizeCat = null;
        if (accountSize < minAccountSize) {
          accountSizeCat = 'small';
        } else if (accountSize >= minAccountSize && accountSize < maxAccountSize) {
          accountSizeCat = 'medium';
        } else if (accountSize >= maxAccountSize) {
          accountSizeCat = 'large';
        }
        if (!progressStats.all[accountSizeCat]) {
          progressStats.all[accountSizeCat] = { count: 0, ids: [] };
        }
        progressStats.all[accountSizeCat].ids = uniq(concat(progressStats.all[accountSizeCat].ids, id));
        progressStats.all[accountSizeCat].count = progressStats.all[accountSizeCat].ids.length;

        each(resource.categories, (cat) => {
          if (progressStats[cat]?.[accountSizeCat]) {
            progressStats[cat][accountSizeCat].ids = uniq(concat(progressStats[cat][accountSizeCat].ids, id));
            progressStats[cat][accountSizeCat].count = progressStats[cat][accountSizeCat].ids.length;
          }
        });
      });




      return {
        ...state,
        progressStats: {
          ...progressStats,
        },
      };
    }
    case DASHBOARD_CHECK_QUALIFIED_CHUNK: {
      const { isAccount, searchId, chunk, isReset, revenueMin, revenueMax, error } = action;
      
      let resources = isAccount ? state.winLoss.accounts : state.winLoss.opportunities;
      let defaultBounds = [];
      let bounds = [];
      if (isReset) {
        bounds = (uniq(compact(map(state.winLoss.accounts, 'annual_revenue')))).sort((a, b) => { return a > b; }); // for accounts based
        const lowerIndex = Math.floor((state.winLoss.accounts.length / 3) - 1);
        const upperIndex = Math.floor((2 * (state.winLoss.accounts.length / 3)) - 1);
        const lowerBound = bounds[lowerIndex]; // default;
        const upperBound = bounds[upperIndex]; // default;
        defaultBounds = [lowerBound, upperBound];
        const nRS = reduce(resources, (items, resource) => {
          const nR = {
            ...resource,
            checkedQualified: false,
            matched: null,
            submitted: null,
            error: null,
            category: null,
          };
          items.push(nR);
          return items;
        }, []);
        resources = nRS;
      } else if (searchId !== state.winLoss.progressSearchId) {
        // not reset, but searchId is not current searchId
        return state;
      }

      if (error) {
        return {
            ...state,
            winLoss: {
              ...state.winLoss,
              progressLoadPercentage: 0,
              ...(isReset ? { progressSearchId: searchId } : {}),
              progressLoadError: true,
            },
        };
      }

      const indexedChunk = chunk ? keyBy(chunk, isAccount ? 'account_id' : 'opportunity_id') : {};
  
      const attrs = [];
      const progressStats = {
        defaultBounds: isReset ? defaultBounds : state.winLoss.progressStats.defaultBounds,
        accountSizes: isReset ? bounds : (state.winLoss.progressStats.accountSizes || []),
        ...cloneDeep(emptyProgressStats),
      };

      let minAccountSize = 0;
      let maxAccountSize = 0;
      if (revenueMin !== null) {
        minAccountSize = revenueMin;
      } else {
        minAccountSize = progressStats.defaultBounds[0] || 0;
      }

      if (revenueMax !== null) {
        maxAccountSize = revenueMax;
      } else {
        maxAccountSize = progressStats.defaultBounds[1] || 0;
      }

      const newResources = reduce(resources, (items, resource) => {
        const id = isAccount ? resource.account_id : resource.opportunity_id;
        const accountSize = isAccount ? resource.annual_revenue : resource.account?.annual_revenue;
        let accountSizeCat = null;
        if (accountSize < minAccountSize) {
          accountSizeCat = 'small';
        } else if (accountSize >= minAccountSize && accountSize < maxAccountSize) {
          accountSizeCat = 'medium';
        } else if (accountSize >= maxAccountSize) {
          accountSizeCat = 'large';
        }

        const dealStage = camelCase(resource.stage);
        const dealType = camelCase(first(resource.types));

        const newResource = indexedChunk[id] ? {
          ...resource,
          ...omit(indexedChunk[id], attrs),
          checkedQualified: true,
        } : resource;

        // stats
        // already checked
        if (newResource.checkedQualified) {
          // already loaded
          const categories = [];
          let category = null;

          if (newResource.matched) {
            categories.push('qualified');
            progressStats.qualified.count += 1;
            // category = 'qualified';
            if (!newResource.submitted) {
              // qualified but not submitted
              categories.push('notSubmitted');
              category = 'notSubmitted';
              if (newResource.error && newResource.error.type !== undefined) {
                const errorType = newResource.error.type;
                /*
                  others: 0,
                  noQualifiedContacts: 0,
                  preventedByCollectorSettings: 0,
                  failedProductConditions: 0,
                  invalidOwners: 0,
                  invalidSenders: 0,
                  invalidAccounts: 0,
                */
                if (errorType === 2) {
                  categories.push('notSubmitted/noQualifiedContacts');
                } else if (errorType === 3) {
                  categories.push('notSubmitted/preventedByCollectorSettings');
                } else if (errorType === 4) {
                  categories.push('notSubmitted/failedProductConditions');
                } else if (errorType === 5) {
                  categories.push('notSubmitted/invalidOwners');
                } else if (errorType === 6) {
                  categories.push('notSubmitted/invalidSenders');
                } else if (errorType === 7) {
                  categories.push('notSubmitted/invalidAccounts');
                } else {
                  categories.push('notSubmitted/others');
                }
              } else {
                categories.push('notSubmitted/others');
              }
            }
          } else {
            category = 'notQualified';
            categories.push('notQualified');
          }

          // this is for count has_requests, no matter if the opps are qualified/submitted/excluded....
          // newResource.submitted this come from API, only include MATCHED that are submitted
          if (newResource.has_requests) {
            categories.push('allSubmitted'); 
          }

          if (newResource.submitted) {
            categories.push('submitted');
            category = 'submitted';
            /*
            provided feedback: has_feedbacks
            requesting feedback: has_requests
            no response: has_non_responsive
            opted out: has_refused
            excluded: submited && excluded
            */
            const atts = [
              // 'has_feedbacks', 'has_requests', 'has_non_responsive', 'excluded', 'has_refused',
              'has_feedbacks', 'in_outreach', 'has_non_responsive', 'excluded', 'has_refused', 'has_bounced',
            ];
            each(atts, (att) => {
              const k = `submitted/${camelCase(att)}`;
              if (!progressStats[k]) {
                progressStats[k] = cloneDeep(oneProgressStats);
              }
              if (newResource[att]) {
                categories.push(k);
              }
            });
          }

          if (newResource.excluded) {
            categories.push('excluded');
            category = 'excluded';
            const reason = newResource.excludedReason;
            if (reason && reason.reason_id) {
              const k = `excluded/${reason.reason_id}`;
              if (!progressStats[k]) {
                progressStats[k] = {
                  ...cloneDeep(oneProgressStats),
                  reason: reason.reason,
                  type: reason.type,
                  label: reason.label,
                };
              }
              categories.push(k);
            }
          }

          newResource.category = category;
          newResource.categories = categories;
          const ownerId = newResource.owner?.user_id;
          if (ownerId) {
            each([dealStage, 'all'], (attr) => {
              const k = `owners/${attr}`;
              if (!progressStats[k]) {
                progressStats[k] = {};
              }
              if (!progressStats[k][ownerId]) {
                progressStats[k][ownerId] = {
                  ...resource.owner,
                  all: { count: 0, ids: [] },
                  qualified: { count: 0, ids: [] },
                  submitted: { count: 0, ids: [] },
                  excluded: { count: 0, ids: [] },
                  notSubmitted: { count: 0, ids: [] },
                  notQualified: { count: 0, ids: [] },
                  reminders: { count: 0, ids: [] },
                };
              }
              if (newResource.reminders) {
                if (indexOf(progressStats[k][ownerId].reminders.ids, id) < 0) {
                  // not yet count
                  progressStats[k][ownerId].reminders.ids = uniq(concat(progressStats[k][ownerId].reminders.ids, id));
                  progressStats[k][ownerId].reminders.count += newResource.reminders;
                }
              }
            });
          }
          
          progressStats.all.ids = uniq(concat(progressStats.all.ids, id));
          progressStats.all.count = progressStats.all.ids.length;

          each([dealStage, accountSizeCat, dealType], (attr) => {
            if (!progressStats.all[attr]) {
              progressStats.all[attr] = { count: 0, ids: [] };
            }  
            progressStats.all[attr].ids = uniq(concat(progressStats.all[attr].ids, id));
            progressStats.all[attr].count = progressStats.all[attr].ids.length;
          });

  
          each(categories, (cat) => {
            each([dealStage, accountSizeCat, dealType], (attr) => {
              if (!progressStats[cat][attr]) {
                progressStats[cat][attr] = { count: 0, ids: [] };
              }  
            });

            progressStats[cat].ids = uniq(concat(progressStats[cat].ids, id));
            progressStats[cat].count = progressStats[cat].ids.length;
            if (ownerId) {
              // console.log('>>>>>', ownerId);

              each([dealStage, 'all'], (attr) => {
                const k = `owners/${attr}`;
                each([cat, 'all'], (c) => {
                  if (progressStats[k][ownerId][c]) {
                    progressStats[k][ownerId][c].ids = uniq(concat(progressStats[k][ownerId][c].ids, id));
                    progressStats[k][ownerId][c].count = progressStats[k][ownerId][c].ids.length;
                  }
                });
              });

              if (newResource.reminders && newResource.reminders > 0) {
                const reminderK = 'reminders/owners';
                if (!progressStats[reminderK]) {
                  progressStats[reminderK] = {};
                }
                if (!progressStats[reminderK][ownerId]) {
                  progressStats[reminderK][ownerId] = {
                    ...resource.owner,
                    all: { count: 0, ids: [] },
                    qualified: { count: 0, ids: [] },
                    submitted: { count: 0, ids: [] },
                    excluded: { count: 0, ids: [] },
                    notSubmitted: { count: 0, ids: [] },
                    notQualified: { count: 0, ids: [] },
                    reminders: { count: 0, ids: [] },
                  };
                }
                
                if (indexOf(progressStats[reminderK][ownerId].reminders.ids, id) < 0) {
                  // not yet count
                  progressStats[reminderK][ownerId].reminders.ids = uniq(concat(progressStats[reminderK][ownerId].reminders.ids, id));
                  progressStats[reminderK][ownerId].reminders.count += newResource.reminders;
                }
                each([cat, 'all'], (c) => {
                  if (progressStats[reminderK][ownerId][c]) {
                    progressStats[reminderK][ownerId][c].ids = uniq(concat(progressStats[reminderK][ownerId][c].ids, id));
                    progressStats[reminderK][ownerId][c].count = progressStats[reminderK][ownerId][c].ids.length;
                  }
                });
              }
            }
            
            each([dealStage, dealType, accountSizeCat], (attr) => {
              if (progressStats[cat][attr]) {
                progressStats[cat][attr].ids = uniq(concat(progressStats[cat][attr].ids, id));
                progressStats[cat][attr].count = progressStats[cat][attr].ids.length;
              }
            });

            // owners/all
            // owners/won
            // owners/lost
            // owners/nodecision
            
          });
        }

        items.push(newResource);
        return items;
      }, []);

      const checkedQualified = filter(newResources, { checkedQualified: true });
      const progressLoadPercentage = newResources.length ? checkedQualified.length / newResources.length : 0;
      return {
        ...state,
        winLoss: {
          ...state.winLoss,
          [isAccount ? 'accounts' : 'opportunities']: newResources,
          progressLoadPercentage,
          progressStats,

          ...(isReset ? { progressSearchId: searchId } : {}),
        },
      };
    }
    case DASHBOARD_APPEND_OPPORTUNITY_CONTACT_ROLE: {
      const { opportunity, contact } = action;
      const { winLoss } = state;
      const { opportunities } = winLoss;
      const newOpportunities = reduce(opportunities, (items, opp) => {
        if (opp.opportunity_id === opportunity.opportunity_id) {
          const contactRoles = [...(opportunity.contact_roles || [])];
          const newRole = {
            contact_id: contact.contact_id,
            id: moment().format('X'),
            ...contact.role,
          };
          contactRoles.push(newRole);
          const newOpp = {
            ...opp,
            contact_roles: contactRoles,
          };
          items.push(newOpp);
        } else {
          items.push(opp);
        }
        return items;
      }, []);
      return {
        ...state,
        winLoss: {
          ...winLoss,
          opportunities: newOpportunities,
        },
      };

    }
    default:
      return state;
  }
};

// Get all posts
export const getTeamsLoading = state => state.dashboard.teams.status === 'loading';
export const getTeamsLoaded = state => state.dashboard.teams.status === 'loaded';
export const getTeamsErrors = state => state.dashboard.teams.errors;
export const getTeams = state => state.dashboard.teams.teams;
export const getIndexedUsers = state => state.dashboard.teams.indexedUsers;
export const getIndexedOwners = state => state.dashboard.teams.indexedOwners;

export const getPipelineLoading = state => state.dashboard.pipeline.status === 'loading';
export const getPipelineLoaded = state => state.dashboard.pipeline.status === 'loaded';
export const getPipelineErrors = state => state.dashboard.pipeline.errors;
export const getPipelineOpportunities = state => state.dashboard.pipeline.opportunities;
export const getPipelineStats = state => state.dashboard.pipeline.totalStats;
export const getPipelineOwnerStats = state => state.dashboard.pipeline.indexedOwnerStats;
export const getPipelineRankings = state => state.dashboard.pipeline.rankings;
export const getPipelineAccounts = state => state.dashboard.pipeline.accounts;

export const getConversionLoading = state => state.dashboard.conversion.status === 'loading';
export const getConversionLoaded = state => state.dashboard.conversion.status === 'loaded';
export const getConversionErrors = state => state.dashboard.conversion.errors;
export const getConversionStats = state => state.dashboard.conversion.projectedSales;
export const getConversionOwnerStats = state => state.dashboard.conversion.ownerProjectedSales;
export const getConversionRankings = state => state.dashboard.conversion.rankings;
export const getConversionOpportunities = state => state.dashboard.conversion.opportunities;

export const getForecastLoading = state => state.dashboard.forecast.status === 'loading';
export const getForecastLoaded = state => state.dashboard.forecast.status === 'loaded';
export const getForecastErrors = state => state.dashboard.forecast.errors;
export const getForecastStats = state => state.dashboard.forecast.stats;
export const getForecastOpportunities = state => state.dashboard.forecast.allOpportunities;
export const getForecastOwnerStats = state => state.dashboard.forecast.ownerStats;
export const getForecastRankings = state => state.dashboard.forecast.rankings;

export const getCurrentGlobalRankingsLoading = state => state.dashboard.rankings.status === 'loading';
export const getCurrentGlobalRankingsLoaded = state => state.dashboard.rankings.status === 'loaded';
export const getCurrentGlobalRankingsErrors = state => state.dashboard.rankings.errors;
export const getCurrentGlobalRankings = state => state.dashboard.rankings.owners;

export const getWinLossLoading = state => state.dashboard.winLoss.status === 'loading';
export const getWinLossLoaded = state => state.dashboard.winLoss.status === 'loaded';
export const getWinLossErrors = state => state.dashboard.winLoss.errors;
export const getWinLossStats = state => state.dashboard.winLoss.stats;
export const getWinLossOwnerStats = state => state.dashboard.winLoss.ownerStats;
export const getWinLossAccounts = state => state.dashboard.winLoss.accounts;
export const getWinLossReasonStats = state => state.dashboard.winLoss.reasonStats;
export const getWinLossOpportunities = state => state.dashboard.winLoss.opportunities;
export const getWinLossTotalExclude = state => state.dashboard.winLoss.totalExclude;
export const getWinLossRankings = state => state.dashboard.winLoss.rankings;
export const getWinLossOpportunityFeedbacks = (state, opportunityId) => state.dashboard.winLoss.feedbacks[opportunityId];
export const getWinLossOpportunitySurveys = (state, opportunityId) => state.dashboard.winLoss.surveys[opportunityId];
export const getWinLossOpportunityInternalEmails = (state, opportunityId) => state.dashboard.winLoss.internalEmails[opportunityId];
export const getWinLossEditingOpportunity = state => state.dashboard.winLoss.editingOpportunity;
export const getWinLossEditingAccount = state => state.dashboard.winLoss.editingAccount;

export const getWinLossFeedbackLoaded = state => state.dashboard.winLoss.feedbackStatus === 'loaded';

export const getWinLossCurrentInsightLoading = state => state.dashboard.winLoss.currentInsight.status === 'loading';
export const getWinLossCurrentInsightLoaded = state => state.dashboard.winLoss.currentInsight.status === 'loaded';
export const getWinLossCurrentInsightErrors = state => state.dashboard.winLoss.currentInsight.errors;
export const getWinLossCurrentInsights = state => state.dashboard.winLoss.currentInsight.data;
export const getWinLossCurrentInsightShow = state => state.dashboard.winLoss.currentInsight.show;
export const getWinLossCurrentEmail = state => state.dashboard.winLoss.currentEmail;
export const getWinLossCollectorStats = state => state.dashboard.winLoss.indexedCollectorStats;
export const getWinLossStatsFilters = state => state.dashboard.winLoss.statsFilters;

export const getWinLossGlobalRankingsLoading = state => state.dashboard.winLossRankings.status === 'loading';
export const getWinLossGlobalRankingsLoaded = state => state.dashboard.winLossRankings.status === 'loaded';
export const getWinLossGlobalRankingsErrors = state => state.dashboard.winLossRankings.errors;
export const getWinLossGlobalRankings = state => state.dashboard.winLossRankings.owners;
export const getFeedbackRequestSettings = state => state.dashboard.settings;
export const getFeedbackRequestStatus = (state, opportunityId) => state.dashboard.feedbackRequestStatus[opportunityId];

export const getAccountResults = (state, accountId) => state.dashboard.winLoss.accountResults[accountId];
export const getAccountJobs = (state, accountId) => state.dashboard.winLoss.accountJobs[accountId];

export const getCurrentCollectorSummary = state => state.dashboard.winLoss.currentCollectorSummary || [];



export const getExclusionLoading = state => state.dashboard.exclusions.status === 'loading';
export const getExclusionLoaded = state => state.dashboard.exclusions.status === 'loaded';
export const getExclusionErrors = state => state.dashboard.exclusions.errors;
export const getExclusionOpportunities = state => state.dashboard.exclusions.opportunities;
export const getExclusionStats = state => state.dashboard.exclusions.stats;

export const getContactExclusionLoading = state => state.dashboard.excludedContacts.status === 'loading';
export const getContactExclusionLoaded = state => state.dashboard.excludedContacts.status === 'loaded';
export const getContactExclusionErrors = state => state.dashboard.excludedContacts.errors;
export const getContactExclusions = state => state.dashboard.excludedContacts.contacts;
export const getProgressLoadPercentage = state => state.dashboard.winLoss.progressLoadPercentage;
export const getProgressStats = state => state.dashboard.winLoss.progressStats;
export const getProgressLoadError = state => state.dashboard.winLoss.progressLoadError;



export const getContactProgressLoading = state => state.dashboard.progressContacts.status === 'loading';
export const getContactProgressLoaded = state => state.dashboard.progressContacts.status === 'loaded';
export const getContactProgressErrors = state => state.dashboard.progressContacts.errors;
export const getProgressContacts = state => state.dashboard.progressContacts.contacts;

export const getShouldShowAllStats = (team, auth, organization, module) => {
  let showTeamStats = false;
  if (team && team.user__id === auth._id) {
    // the login user is the creator of this team, he can see everything
    showTeamStats = true;
  } else if (auth && (auth.role === 'admin' || auth.role === 'superadmin')) {
    showTeamStats = true;
  } else if (auth && (auth.organization_role === 'admin' || auth.organization_role === 'manager')) {
    showTeamStats = true;
  } else if (auth && auth.role === 'pi' && indexOf(auth.organization__ids, organization._id) >= 0) {
    showTeamStats = true;
  } else if (organization.role_access_rules && organization.role_access_rules[auth.organization_role] && organization.role_access_rules[auth.organization_role][module] === 'ALL') {
    showTeamStats = true;
  }
  return showTeamStats;
}

export const getCollectorStats = (state, collectorId) => {
  const stats = state.dashboard.collectorStats[collectorId];
  return stats;
};
export const getCollectorStatsLoading = (state, collectorId) => {
  const stats = getCollectorStats(state, collectorId);
  return stats ? stats.status === 'loading' : false;
};
export const getCollectorStatsLoaded = (state, collectorId) => {
  const stats = getCollectorStats(state, collectorId);
  return stats ? stats.status === 'loaded' : false;
};
export const getCollectorStatsData = (state, collectorId) => {
  const stats = getCollectorStats(state, collectorId);
  return stats ? stats.data : {};
};

// Export Reducer
export default DashboardReducer;
